// Activity log — what each person actually did in the ERP, per day.
//
// VISIBILITY: admin + management see everyone (roster + anyone's day). Every other role sees only
// themselves — enforced on the server, not here; this file just doesn't render what it won't get.
//
// The limits are stated ON THE SCREEN, not buried in docs, because the single most likely misuse
// of this page is treating a low count as proof someone did nothing. Reads are invisible.
import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { addDays } from '../utils/localDate';

const C = { navy: '#3B3A8F', text: '#1a1a2e', muted: '#888', border: '#eee',
            red: '#A32D2D', amber: '#9A6B00', green: '#166534', violet: '#EEEDF8' };
const card = { background: '#fff', border: `1px solid ${C.border}`, borderRadius: 10, padding: 18, marginBottom: 16 };
const th = { fontSize: 10, color: C.navy, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.5,
             padding: '8px 10px', textAlign: 'left', background: C.violet, borderBottom: `1px solid ${C.border}`, whiteSpace: 'nowrap' };
const td = { fontSize: 13, color: C.text, padding: '8px 10px', borderBottom: '1px solid #f4f4f4' };
const btn = (on) => ({ padding: '6px 12px', fontSize: 12, borderRadius: 6, cursor: 'pointer',
  border: `1px solid ${on ? C.navy : '#ddd'}`, background: on ? C.navy : '#fff', color: on ? '#fff' : '#555', fontWeight: on ? 700 : 500 });

const today = () => new Date().toISOString().slice(0, 10);
// Calendar days on the LOCAL date (16 Oct 2026). Reading a local midnight back through toISOString() made
// "Next" stand still and "Previous" jump two days in IST.
const shift = (d, n) => addDays(d, n);
const pretty = (d) => {
  if (d === today()) return `Today · ${d}`;
  if (d === shift(today(), -1)) return `Yesterday · ${d}`;
  return new Date(d + 'T00:00:00').toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'short', year: 'numeric' });
};

// ── The standing caveat. Shown wherever a count is, because a count without it invites the
// wrong conclusion — that a quiet row means an idle person.
const Caveat = ({ compact }) => (
  <div style={{ background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 8,
    padding: compact ? '8px 12px' : '12px 16px', fontSize: 12, color: '#78500A', lineHeight: 1.65 }}>
    <strong>What this cannot see.</strong>{' '}
    <strong>Reads are invisible.</strong> Someone who opens the ERP every day to review dashboards,
    check approvals and read reports — and writes nothing — appears here <em>identical to someone who
    never logged in</em>. A low count is not evidence of an idle person; it is evidence of few
    recorded writes.{!compact && <>
    {' '}Edits, deletions, and approvals that changed nothing are largely unrecorded. Work done
    outside the ERP — the gate register, phone calls, WhatsApp approvals — is not here at all.</>}
  </div>
);

export default function Activity() {
  const { user } = useAuth();
  const canSeeAll = ['admin', 'management'].includes(user?.role);
  const [tab, setTab] = useState(canSeeAll ? 'roster' : 'me');
  const [meta, setMeta] = useState(null);

  useEffect(() => { api.get('/activity/meta').then(r => setMeta(r.data.data)).catch(() => {}); }, []);

  return (
    <div style={{ padding: 24, maxWidth: 1150, margin: '0 auto' }}>
      <h2 style={{ margin: '0 0 4px', color: C.text }}>📋 Activity Log</h2>
      <div style={{ fontSize: 12.5, color: C.muted, marginBottom: 16 }}>
        What each person recorded in the ERP, day by day.
        {canSeeAll
          ? ' You can see everyone.'
          : ' You can see your own activity.'}
      </div>

      {canSeeAll && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          <button onClick={() => setTab('roster')} style={btn(tab === 'roster')}>Everyone</button>
          <button onClick={() => setTab('me')} style={btn(tab === 'me')}>My activity</button>
        </div>
      )}

      {tab === 'roster' && canSeeAll ? <Roster meta={meta} /> : <Person userId={user?.id} self meta={meta} />}
    </div>
  );
}

// ── Roster: every active user, sorted by STALENESS ──────────────────────────
function Roster({ meta }) {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState('');
  const [open, setOpen] = useState(null);   // user being drilled into

  useEffect(() => {
    api.get('/activity/roster')
      .then(r => setRows(r.data.data || []))
      .catch(e => setErr(e.response?.data?.error || 'Could not load the roster'));
  }, []);

  if (err) return <div style={{ ...card, color: C.red }}>{err}</div>;
  if (!rows) return <div style={{ ...card, color: C.muted }}>Loading…</div>;

  const never = rows.filter(r => !r.last_write).length;
  const stale = rows.filter(r => r.days_since_write !== null && r.days_since_write >= 7).length;

  return (
    <>
      <div style={{ marginBottom: 16 }}><Caveat /></div>

      <div style={{ ...card, display: 'flex', gap: 34, flexWrap: 'wrap' }}>
        {[['Active users', rows.length, null],
          ['Never written', never, never ? C.red : C.green],
          ['Silent 7+ days', stale, stale ? C.amber : C.green],
          ['Wrote today', rows.filter(r => r.writes_today > 0).length, null]].map(([l, v, tone]) => (
          <div key={l}>
            <div style={{ fontSize: 10, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 800 }}>{l}</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: tone || C.navy, marginTop: 2 }}>{v}</div>
          </div>
        ))}
      </div>

      <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: '12px 16px', borderBottom: `1px solid ${C.border}` }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: C.navy }}>Everyone — sorted by staleness</div>
          <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
            Longest-silent first. Deliberately not sorted by volume — a big number is not the question.
          </div>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>
              {['Person', 'Role', 'Last write', 'Silent for', '30 days', 'Today', 'Yesterday', 'Areas', 'Last login', ''].map(h =>
                <th key={h} style={th}>{h}</th>)}
            </tr></thead>
            <tbody>
              {rows.map(r => {
                const d = r.days_since_write;
                const tone = r.last_write === null ? C.red : d >= 14 ? C.red : d >= 7 ? C.amber : C.text;
                return (
                  <React.Fragment key={r.id}>
                    <tr>
                      <td style={{ ...td, fontWeight: 700 }}>{r.name}</td>
                      <td style={{ ...td, color: C.muted }}>{r.role}</td>
                      <td style={{ ...td, color: tone, fontWeight: r.last_write ? 500 : 700 }}>
                        {r.last_write ? String(r.last_write).slice(0, 10) : 'never'}
                      </td>
                      <td style={{ ...td, color: tone, fontWeight: 700 }}>
                        {r.last_write ? `${d}d` : '—'}
                      </td>
                      <td style={{ ...td, fontWeight: 700 }}>{r.writes_30d}</td>
                      <td style={td}>{r.writes_today || <span style={{ color: '#ccc' }}>0</span>}</td>
                      <td style={td}>{r.writes_yesterday || <span style={{ color: '#ccc' }}>0</span>}</td>
                      <td style={{ ...td, color: C.muted }}>{r.distinct_areas}</td>
                      {/* Last login is deliberately last and unstyled — it is NOT attendance. */}
                      <td style={{ ...td, color: C.muted, fontSize: 12 }}>
                        {r.last_login_at ? String(r.last_login_at).slice(0, 10) : '—'}
                      </td>
                      <td style={td}>
                        <button onClick={() => setOpen(open === r.id ? null : r.id)} style={btn(open === r.id)}>
                          {open === r.id ? 'Hide' : 'Days'}
                        </button>
                      </td>
                    </tr>
                    {open === r.id && (
                      <tr><td colSpan={10} style={{ padding: 16, background: '#FAFAFC', borderBottom: `1px solid ${C.border}` }}>
                        <Person userId={r.id} name={r.name} meta={meta} embedded />
                      </td></tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div style={{ fontSize: 11.5, color: C.muted, lineHeight: 1.7 }}>
        <strong>“Last login” is the last login only</strong> — a single timestamp overwritten each time.
        There is no login history and no frequency, so it cannot tell you whether someone signs in daily
        or signed in once. Do not read it as attendance.
      </div>
    </>
  );
}

// ── One person: a day, and the days around it ───────────────────────────────
function Person({ userId, name, self, meta, embedded }) {
  const [date, setDate] = useState(today());
  const [day, setDay] = useState(null);
  const [days, setDays] = useState([]);
  const [err, setErr] = useState('');

  const params = useCallback((extra) => (self ? { ...extra } : { user_id: userId, ...extra }), [self, userId]);

  useEffect(() => {
    setDay(null); setErr('');
    api.get('/activity/day', { params: params({ date }) })
      .then(r => setDay(r.data.data))
      .catch(e => setErr(e.response?.data?.error || 'Could not load activity'));
  }, [date, params]);

  useEffect(() => {
    api.get('/activity/days', { params: params({ days: 30 }) })
      .then(r => setDays(r.data.data || [])).catch(() => setDays([]));
  }, [params]);

  const byModule = {};
  (day?.items || []).forEach(i => { (byModule[i.module] = byModule[i.module] || []).push(i); });
  const modules = Object.keys(byModule).sort();

  return (
    <div>
      {!embedded && <div style={{ marginBottom: 16 }}><Caveat /></div>}

      <div style={{ ...card, marginBottom: 12 }}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <button onClick={() => setDate(shift(date, -1))} style={btn(false)}>← Previous</button>
          <input type="date" value={date} max={today()} onChange={e => setDate(e.target.value)}
            style={{ padding: '6px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13 }} />
          <button onClick={() => setDate(shift(date, 1))} disabled={date >= today()} style={btn(false)}>Next →</button>
          <button onClick={() => setDate(today())} style={btn(date === today())}>Today</button>
          <button onClick={() => setDate(shift(today(), -1))} style={btn(date === shift(today(), -1))}>Yesterday</button>
        </div>
      </div>

      <div style={card}>
        <div style={{ fontSize: 14, fontWeight: 800, color: C.navy, marginBottom: 2 }}>
          {name || day?.user?.name || 'You'} — {pretty(date)}
        </div>
        {err && <div style={{ color: C.red, fontSize: 13, marginTop: 8 }}>{err}</div>}
        {!err && !day && <div style={{ color: C.muted, fontSize: 13, marginTop: 8 }}>Loading…</div>}

        {day && day.total === 0 && (
          <div style={{ marginTop: 10, fontSize: 13, color: C.muted, lineHeight: 1.7 }}>
            <strong style={{ color: C.text }}>Nothing recorded on this day.</strong><br />
            That means no created, approved, verified or edited record carries this person's name for
            this date. It does <em>not</em> mean they were absent or idle — reading, reviewing and
            checking leave no trace.
          </div>
        )}

        {day && day.total > 0 && (
          <>
            <div style={{ fontSize: 12, color: C.muted, marginBottom: 12 }}>
              {day.total} recorded action{day.total === 1 ? '' : 's'} across {modules.length} area{modules.length === 1 ? '' : 's'}
            </div>
            {modules.map(m => (
              <div key={m} style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 10.5, fontWeight: 800, color: C.navy, textTransform: 'uppercase',
                  letterSpacing: 0.6, marginBottom: 5 }}>{m}</div>
                {byModule[m].map((i, ix) => (
                  <div key={ix} style={{ fontSize: 13.5, color: C.text, padding: '3px 0', lineHeight: 1.6 }}>
                    · {i.text}
                    <span style={{ color: C.muted, fontSize: 11.5, marginLeft: 8 }}>
                      {String(i.first_at).slice(11, 16)}
                      {i.n > 1 && i.last_at !== i.first_at && `–${String(i.last_at).slice(11, 16)}`}
                    </span>
                    {/* 25 of 184 actor pairs have no timestamp of their own. Saying so beats
                        presenting a creation date as though it were the moment of the act. */}
                    {i.approximate && (
                      <span title="This record has no timestamp of its own, so the time shown is when the record was created — the action may have happened later."
                        style={{ marginLeft: 8, fontSize: 10.5, color: C.amber, border: '1px solid #FDE68A',
                          background: '#FFFBEB', padding: '1px 5px', borderRadius: 4 }}>approx. time</span>
                    )}
                  </div>
                ))}
              </div>
            ))}
            {day.any_approximate && (
              <div style={{ fontSize: 11, color: C.amber, marginTop: 6 }}>
                Items marked <strong>approx. time</strong> are dated to when the record was created, not
                when the action was taken — an approval made a week later shows the earlier date.
              </div>
            )}
          </>
        )}
      </div>

      {/* Last 30 days at a glance — click a day to open it. */}
      <div style={{ ...card, padding: 14 }}>
        <div style={{ fontSize: 12, fontWeight: 800, color: C.navy, marginBottom: 10 }}>Last 30 days</div>
        <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
          {[...days].reverse().map(d => {
            const n = Number(d.n);
            const bg = n === 0 ? '#F1F1F4' : n < 5 ? '#DDEBFF' : n < 20 ? '#9FC2F5' : n < 60 ? '#5B8FE0' : C.navy;
            return (
              <button key={d.day} onClick={() => setDate(String(d.day).slice(0, 10))}
                title={`${String(d.day).slice(0, 10)} — ${n} recorded action${n === 1 ? '' : 's'}`}
                style={{ width: 26, height: 26, borderRadius: 4, background: bg, cursor: 'pointer',
                  border: String(d.day).slice(0, 10) === date ? `2px solid ${C.text}` : '1px solid #e6e6ea',
                  fontSize: 9, color: n >= 20 ? '#fff' : '#555', fontWeight: 700, padding: 0 }}>
                {n || ''}
              </button>
            );
          })}
        </div>
        <div style={{ fontSize: 11, color: C.muted, marginTop: 8 }}>
          Empty squares are days with no recorded write — which includes weekends, leave, and days
          spent entirely on work the ERP does not capture.
        </div>
      </div>

      {meta && !embedded && (
        <div style={{ fontSize: 11, color: C.muted, lineHeight: 1.7 }}>
          Built live from <strong>{meta.pairs}</strong> actor columns across <strong>{meta.tables}</strong> tables
          ({meta.exact_pairs} with their own timestamp, {meta.approx_pairs} approximate). Columns naming the
          <em> subject</em> of a record — whose attendance, whose payslip, who a notification was sent to — are
          excluded, because they are not actions that person took.
        </div>
      )}
    </div>
  );
}
